import { logger } from '@/logger.js';

/** The part of Chromium's `BeforeInstallPromptEvent` the promotion uses. */
interface DeferredInstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** What the page knows about installing itself right now. */
export interface InstallPromptState {
  /** The prompt Chromium handed over, kept until a click uses it. */
  prompt: DeferredInstallPrompt | null;
  /** The app was installed during this page's life. */
  installed: boolean;
}

let state: InstallPromptState = { prompt: null, installed: false };
let capturedOn: Window | null = null;
const listeners = new Set<() => void>();

/** Replaces the state and tells every subscriber. */
function update(next: InstallPromptState): void {
  state = next;
  for (const listener of listeners) listener();
}

/**
 * Starts listening at boot, before React renders: Chromium can hand over its prompt as soon as the
 * page loads, and a late listener misses it. The default mini-infobar is suppressed so the prompt
 * only ever opens from a click (MEDA-1187).
 */
export function captureInstallPrompt(target: Window = window): void {
  if (capturedOn === target) return;
  capturedOn = target;
  target.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    update({ ...state, prompt: event as DeferredInstallPrompt });
  });
  target.addEventListener('appinstalled', () => {
    logger.info('install_promotion', { action: 'installed' });
    update({ prompt: null, installed: true });
  });
}

/** Subscribes to changes, for `useSyncExternalStore`. */
export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current state, for `useSyncExternalStore`. */
export function installPromptState(): InstallPromptState {
  return state;
}

/**
 * Opens the browser's install dialog and logs the person's answer. A prompt works once, so it is
 * dropped after use; Chromium hands over a new one if the person can still install.
 */
export async function runInstallPrompt(): Promise<void> {
  const deferred = state.prompt;
  if (!deferred) return;
  update({ ...state, prompt: null });
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  logger.info('install_promotion', {
    action: 'choice',
    route: 'prompt',
    outcome,
  });
}

/** Forgets the captured prompt and the installed flag, keeping the listeners; tests only. */
export function resetInstallPrompt(): void {
  state = { prompt: null, installed: false };
}
